import * as React from "react";
import { useCallback } from "react";
import { Box, TableRow, TableCell, IconButton, Tooltip, Checkbox } from "@mui/material";
import FormHasErrorsIcon from "components/icons/iconComponents/FormHasErrorsIcon";
import DeleteIcon from "components/icons/iconComponents/DeleteIcon";
import ordinaryColors from "themes/ordinaryColors";
import { TimePowerParameters } from "pages/UploadCertificate/types";
import { MatrixSection } from "./matrixDataUtils";
import { StyledRowHeaderCell, StyledTextField } from "./styledComponents";
import { FieldIndices } from "./useMatrixValidation";
// import { translations } from "translations";

// Error tooltip component for input fields - returns children only (no icon)
const ErrorTooltip: React.FC<{
  error: boolean;
  errorText: string;
  children: React.ReactElement;
}> = ({ children }) => {
  return children;
};

// Row error icon component - shows error icon in front of row label
const RowErrorIcon: React.FC<{
  hasError: boolean;
  errorText: string;
  children: React.ReactNode;
}> = ({ hasError, errorText, children }) => {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {hasError && (
        <Tooltip
          title={errorText}
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: `${ordinaryColors.errorMain} !important`,
                color: "white",
                fontSize: "14px",
                padding: "8px 12px",
                borderRadius: "4px",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              mr: 1,
            }}
          >
            <FormHasErrorsIcon />
          </Box>
        </Tooltip>
      )}
      {children}
    </div>
  );
};

/** Callback signatures used by row sub-components */
export interface MatrixRowCallbacks {
  handleInputChange: (params: {
    section: MatrixSection;
    field: keyof TimePowerParameters;
    value: string;
    index?: number;
    zwIndex?: number;
  }) => void;
  handleInputBlur: (params: {
    section: MatrixSection;
    field: keyof TimePowerParameters;
    index?: number;
    zwIndex?: number;
  }) => void;
  getDisplayValue: (section: MatrixSection, field: keyof TimePowerParameters, indices?: FieldIndices) => string;
  hasRowError: (section: MatrixSection, index?: number) => boolean;
  getRowErrorText: (section: MatrixSection, index?: number) => string;
  hasFieldError: (section: MatrixSection, field: keyof TimePowerParameters, indices?: FieldIndices) => boolean;
  getFieldErrorText: (section: MatrixSection, field: keyof TimePowerParameters, indices?: FieldIndices) => string;
}

// Decimal input field
const DecimalInput: React.FC<{
  section: MatrixSection;
  field: keyof TimePowerParameters;
  index?: number;
  zwIndex?: number;
  value: string;
  onChange: MatrixRowCallbacks["handleInputChange"];
  onBlur: MatrixRowCallbacks["handleInputBlur"];
  disabled?: boolean;
  hasError?: boolean;
  errorText?: string;
  allowNegative?: boolean;
}> = ({
  section,
  field,
  index,
  zwIndex,
  value,
  onChange,
  onBlur,
  disabled,
  hasError = false,
  errorText = "",
  allowNegative = false,
}) => {
  // const getPlaceholder = () => {
  //   if (field !== "taValue") return "";
  //   if (section === "taMin") return "T_A,min";
  //   if (section === "taMax") return "T_A,max";
  //   if (section === "taRange" && typeof index === "number") return `T_A,${index + 1}`;
  //   return "";
  // };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e?.preventDefault();
      const inputValue = e.target.value;
      const allowedChars = allowNegative ? /^-?[0-9.,]*$/ : /^[0-9.,]*$/;
      if (allowedChars.test(inputValue) || inputValue === "") {
        onChange({ section, field, value: inputValue, index, zwIndex });
      }
    },
    [section, field, index, zwIndex, onChange, allowNegative]
  );

  const handleBlur = useCallback(() => {
    onBlur({ section, field, index, zwIndex });
  }, [section, field, index, zwIndex, onBlur]);

  return (
    <ErrorTooltip error={hasError} errorText={errorText}>
      <StyledTextField
        type="text"
        size="small"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        error={hasError}
        // placeholder={translations.pages.certificateUpload.enterValue} // {getPlaceholder()}
        sx={{ width: 80, whiteSpace: "pre-wrap" }}
      />
    </ErrorTooltip>
  );
};

// Power parameter input cell
const PowerParameterCell: React.FC<{
  section: MatrixSection;
  field: keyof TimePowerParameters;
  index?: number;
  zwIndex?: number;
  value: string;
  onChange: MatrixRowCallbacks["handleInputChange"];
  onBlur: MatrixRowCallbacks["handleInputBlur"];
  disabled?: boolean;
  hasError?: boolean;
  errorText?: string;
  allowNegative?: boolean;
}> = ({ section, field, index, zwIndex, value, onChange, onBlur, disabled, hasError, errorText, allowNegative }) => (
  <TableCell align="center">
    <DecimalInput
      section={section}
      field={field}
      index={index}
      zwIndex={zwIndex}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      hasError={hasError}
      errorText={errorText}
      allowNegative={allowNegative}
    />
  </TableCell>
);

/** Static power fields (everything except the dynamic Zw impedance columns) */
const STATIC_POWER_FIELDS: (keyof TimePowerParameters)[] = [
  "pMinDyn",
  "pMaxDyn",
  "pGrenzNegMin",
  "pGrenzNegMax",
  "pGrenzPosMin",
  "pGrenzPosMax",
];

/** A single matrix parameter table row with label, optional delete button, T_A cell, N impedance cells, and 6 power cells */
const MatrixParameterRow: React.FC<
  {
    rowLabel: string;
    section: MatrixSection;
    index?: number;
    data: TimePowerParameters;
    zwColumnCount: number;
    showAddZwColumn?: boolean;
    disabled?: boolean;
    onDelete?: () => void;
  } & MatrixRowCallbacks
> = ({
  rowLabel,
  section,
  index,
  data,
  zwColumnCount,
  showAddZwColumn,
  hasRowError,
  getRowErrorText,
  getDisplayValue,
  handleInputChange,
  handleInputBlur,
  hasFieldError,
  getFieldErrorText,
  disabled,
  onDelete,
}) => (
  <TableRow>
    <StyledRowHeaderCell component="th" scope="row">
      <RowErrorIcon hasError={hasRowError(section, index)} errorText={getRowErrorText(section, index)}>
        <Box dangerouslySetInnerHTML={{ __html: rowLabel }} />
      </RowErrorIcon>
    </StyledRowHeaderCell>
    <TableCell align="center">
      {onDelete && (
        <IconButton onClick={onDelete} size="small" disabled={disabled}>
          <DeleteIcon disabled={disabled} />
        </IconButton>
      )}
    </TableCell>
    {/* T_A value column */}
    <PowerParameterCell
      section={section}
      field="taValue"
      index={index}
      value={getDisplayValue(section, "taValue", { index })}
      onChange={handleInputChange}
      onBlur={handleInputBlur}
      disabled={disabled}
      hasError={hasFieldError(section, "taValue", { index })}
      errorText={getFieldErrorText(section, "taValue", { index })}
    />
    {/* Static power fields */}
    {STATIC_POWER_FIELDS.map((field) => (
      <PowerParameterCell
        key={String(field)}
        section={section}
        field={field}
        index={index}
        value={getDisplayValue(section, field, { index })}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        disabled={disabled}
        hasError={hasFieldError(section, field, { index })}
        errorText={getFieldErrorText(section, field, { index })}
        allowNegative
      />
    ))}
    {/* Dynamic Zw impedance columns — checkboxes */}
    {Array.from({ length: zwColumnCount }, (_, zwIdx) => (
      <TableCell key={`zw-${zwIdx}`} align="center">
        <Checkbox
          checked={zwIdx === 0 ? true : data.effectiveImpedances?.[zwIdx] === true}
          onChange={() =>
            handleInputChange({
              section,
              field: "effectiveImpedances",
              value: data.effectiveImpedances?.[zwIdx] === true ? "false" : "true",
              index,
              zwIndex: zwIdx,
            })
          }
          disabled={zwIdx === 0 || disabled || section === "taMin" || section === "taMax"}
          size="small"
        />
      </TableCell>
    ))}
    {/* Spacer cell to match the "Add Zw column" header when shown */}
    {showAddZwColumn && <TableCell />}
  </TableRow>
);

export default MatrixParameterRow;
